import { Component, inject } from "@angular/core";
import { GroupConfigurationTableComponent } from "@components/group-configuration-table/group-configuration-table.component";
import { FloorplanItemReference } from "@domain/project/floorplan-item-reference";
import { Floorplan, ProductTypesInGroup } from "@domain/project/floorplan/floorplan";
import { getFloorplanItems, getKindForItem } from "@domain/project/floorplan/floorplan-item.utils";
import { ProductItemGroup } from "@domain/project/floorplan/product-item-group";
import type { ScopedProject } from "@domain/project/project-guards";
import { AreaHeaderComponent } from "@odx/angular/components/area-header";
import { ButtonComponent } from "@odx/angular/components/button";
import {
  ModalContentDirective,
  ModalDismissDirective,
  ModalFooterComponent,
  ModalHeaderComponent,
  ModalRef,
} from "@odx/angular/components/modal";

@Component({
  selector: "app-add-products-to-group-modal",
  standalone: true,
  imports: [
    AreaHeaderComponent,
    ButtonComponent,
    ModalHeaderComponent,
    ModalContentDirective,
    ModalFooterComponent,
    ModalDismissDirective,
    GroupConfigurationTableComponent,
  ],
  templateUrl: "./add-products-to-group-modal.component.html",
  styleUrl: "./add-products-to-group-modal.component.scss",
})
export class AddProductsToGroupModalComponent {
  public readonly modalRef = inject(ModalRef<any, FloorplanItemReference[]>);
  protected productsToDisplay: ProductTypesInGroup[];
  protected project: ScopedProject;
  private selectedProducts: ProductTypesInGroup[] = [];
  private floorplan: Floorplan;

  constructor() {
    this.floorplan = this.modalRef.data.floorplan;
    this.project = this.modalRef.data.project;
    this.productsToDisplay = this.getAllProducts().filter((p) => !this.isAlreadyReferenced(p));
  }

  private isAlreadyReferenced(product: ProductTypesInGroup): boolean {
    const allGroups: ProductItemGroup[] = this.modalRef.data.allGroups ?? [];
    return allGroups.some((group) => group.references.some((ref) => ref.floorplanProductItemId === product.id));
  }

  private getAllProducts(): ProductTypesInGroup[] {
    const floorplans: Floorplan[] = this.project?.cachedFloorplans ?? [this.floorplan];
    return floorplans.flatMap((fp) => getFloorplanItems(fp));
  }

  private getFloorplanForProduct(product: ProductTypesInGroup): Floorplan {
    const floorplans: Floorplan[] = this.project?.cachedFloorplans ?? [this.floorplan];
    return floorplans.find((fp) => getFloorplanItems(fp).some((item) => item.id === product.id)) ?? this.floorplan;
  }

  private getKindForProduct(product: ProductTypesInGroup) {
    return getKindForItem(product);
  }

  onConfirm() {
    const references = this.selectedProducts
      .map((product) => {
        const kind = this.getKindForProduct(product);
        if (!kind) return null;
        const fp = this.getFloorplanForProduct(product);
        return new FloorplanItemReference(fp.id, product.id, kind);
      })
      .filter((ref): ref is FloorplanItemReference => ref !== null);

    this.modalRef.data.group.addReferences(references);
    this.modalRef.close(references);
  }

  protected onSelectedProductsChanged($event: ProductTypesInGroup[]) {
    this.selectedProducts = $event;
  }
}
